import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as FormikForm, Formik } from 'formik';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import RecentActorsIcon from '@material-ui/icons/RecentActors';

import {
  UpdateUserSegmentInput,
  useGetUserSegmentsQuery,
  UserSegmentFragment,
  useUpdateUserSegmentMutation,
} from 'api';
import { Loaders, PaddedPaper } from 'components';
import { SegmentSchema } from 'features/admin/validations';
import { UserSegmentValues } from 'features/admin/types';
import { useNotify } from 'hooks';

import Segment from '../components/Segments/Segment';
import Create from '../components/Segments/Create';

const Segments: FC = () => {
  const { t } = useTranslation();
  const notify = useNotify();

  const { data, error, isLoading } = useGetUserSegmentsQuery(
    {},
    {
      onError: notify.queryError,
    }
  );

  const { mutate: updateUserSegment, isLoading: mutationLoading } = useUpdateUserSegmentMutation({
    onError: notify.mutationError,
    onSuccess: editNotify,
  });

  function getEditValues(values: UserSegmentValues) {
    const { id, title, segment_status } = values;
    return { id, title, segment_status };
  }

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  function handleEdit(values: UpdateUserSegmentInput) {
    updateUserSegment({ data: values });
  }

  function renderCount() {
    const count = isLoading || error ? '--' : data?.getUserSegments?.length;

    return (
      <Box marginTop={5} marginBottom={3}>
        <Typography variant="h5" color="textSecondary">
          {t('admin:yourSegments', { count: count as number })}
        </Typography>
      </Box>
    );
  }

  function renderLoader() {
    return <Loaders.Standard />;
  }

  function renderSegment(segment: UserSegmentFragment) {
    return (
      <Grid key={segment.id} item>
        <Formik
          initialValues={getEditValues(segment)}
          initialTouched={{ title: false }}
          onSubmit={(values, { setTouched }) => {
            handleEdit(values);
            setTouched({});
          }}
          validationSchema={SegmentSchema}
        >
          <FormikForm>
            <Segment segment={segment} working={mutationLoading} />
          </FormikForm>
        </Formik>
      </Grid>
    );
  }

  return (
    <Fragment>
      <PaddedPaper>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <RecentActorsIcon color="primary" fontSize="large" />
          </Grid>
          <Grid item>
            <Typography variant="h4">{t('admin:userSegments')}</Typography>
          </Grid>
        </Grid>
        <Box marginTop={2} marginBottom={2}>
          <Divider />
        </Box>
        <Box marginBottom={3}>
          <Typography variant="subtitle1">{t('admin:userSegmentsHelp')}</Typography>
        </Box>
        <Create />
      </PaddedPaper>
      {renderCount()}
      <Grid container direction="column" spacing={3} wrap="nowrap">
        {isLoading && renderLoader()}
        {!isLoading &&
          data?.getUserSegments?.map(segment => (
            <Grid key={segment.id} item>
              {renderSegment(segment)}
            </Grid>
          ))}
      </Grid>
    </Fragment>
  );
};

export default Segments;
